import React, { useEffect, useLayoutEffect, useRef, useState } from "react";
import { message } from "antd";
import dayjs from "dayjs";
import { TWButton, TWInput, TWModal, TWSelect } from "../../../components/ui";
import api from "../../../services/api";
import fleetApi from "../../../services/fleetApi";
import { fleetErrorText } from "../use-fleet-poll";
import CompanyStoreSelect from "./company-store-select";
import CopyButton from "./copy-button";
import SetupQr from "./setup-qr";
import { fleetApiOrigin } from "./setup-qr-payload";

const DATE_FORMAT = "MMM D, YYYY HH:mm";
const DEFAULT_MAX_USES = "10";
const MAX_USES_ERROR = "Enter a number from 1 to 50";
const ROLE_OPTIONS = [
  { value: "kiosk", label: "Kiosk" },
  { value: "kitchen", label: "Kitchen" },
];
const EMPTY_SCOPE = { companyid: "", storeid: "" };
const LOADING_RELEASE = { status: "loading", release: null };

// "ABCD2345" -> "ABCD-2345" (the API sends the 8 characters without a dash).
const formatCode = (code) =>
  code && code.length === 8 ? `${code.slice(0, 4)}-${code.slice(4)}` : code;

const validMaxUses = (value) => {
  if (!/^\d+$/.test(String(value))) return false;
  const n = Number(value);
  return n >= 1 && n <= 50;
};

const timesText = (n) => `${n} ${Number(n) === 1 ? "time" : "times"}`;

// The public address of the Current agent APK, for an installer who opens it
// in Chrome on the tablet (R-22). On a fleet API host that is the fixed
// `${origin}/agent.apk` redirect; on a local or unknown base it is the release
// file URL itself. Nothing renders without a Current release (its fileurl).
const AgentApkRow = ({ url, apiOrigin }) => {
  if (!url) return null;
  const shown = apiOrigin ? `${apiOrigin}/agent.apk` : url;
  return (
    <div>
      <p className="text-sm text-slate-500 dark:text-slate-400">
        Install the agent from
      </p>
      <div className="mt-1.5 flex items-center gap-2">
        <TWInput
          aria-label="Agent APK link"
          value={shown}
          readOnly
          className="truncate font-mono text-xs"
          wrapperClassName="min-w-0 flex-1"
          onFocus={(e) => e.target.select()}
        />
        <CopyButton text={shown} />
      </div>
      {apiOrigin ? (
        <p className="mt-1 text-xs text-slate-500 dark:text-slate-400">
          Type this address in the tablet's browser; it always serves the
          Current agent.
        </p>
      ) : null}
    </div>
  );
};

// State 1: the form. State 2 (after Create): the code, shown once, in place
// and not dismissible; Done is the only way out.
const EnrollmentCodeModal = ({ open, onClose, onCreated }) => {
  const [scope, setScope] = useState(EMPTY_SCOPE);
  const [role, setRole] = useState("");
  const [maxUses, setMaxUses] = useState(DEFAULT_MAX_USES);
  const [note, setNote] = useState("");
  const [creating, setCreating] = useState(false);
  const [formError, setFormError] = useState("");
  const [created, setCreated] = useState(null); // { code, expiresat, maxuses }
  const [storeName, setStoreName] = useState("");
  // The Current agent release (QR-7): loading | failed | none | ready.
  const [agentRelease, setAgentRelease] = useState(LOADING_RELEASE);
  const submitRef = useRef(false);

  // Each opening starts from a clean form (a layout effect: no painted frame
  // of the previous code).
  useLayoutEffect(() => {
    if (!open) return;
    setScope(EMPTY_SCOPE);
    setRole("");
    setMaxUses(DEFAULT_MAX_USES);
    setNote("");
    setCreating(false);
    setFormError("");
    setCreated(null);
    setStoreName("");
    submitRef.current = false;
  }, [open]);

  // The Current agent release; a failure leaves the APK row out and the setup
  // QR block says the releases could not be loaded.
  useEffect(() => {
    if (!open) return undefined;
    let cancelled = false;
    setAgentRelease(LOADING_RELEASE);
    fleetApi
      .listReleases()
      .then((res) => {
        if (cancelled) return;
        const rows = res?.data?.data || [];
        const current = rows.find((r) => r.app === "agent" && r.iscurrent);
        setAgentRelease(
          current
            ? { status: "ready", release: current }
            : { status: "none", release: null },
        );
      })
      .catch(() => {
        if (!cancelled) setAgentRelease({ status: "failed", release: null });
      });
    return () => {
      cancelled = true;
    };
  }, [open]);

  const apkUrl = agentRelease.release?.fileurl || "";
  const apiOrigin = fleetApiOrigin(api.defaults.baseURL);
  const maxUsesOk = validMaxUses(maxUses);
  const canCreate = Boolean(scope.storeid) && Boolean(role) && maxUsesOk;

  const create = async () => {
    if (submitRef.current || !canCreate) return;
    submitRef.current = true;
    setCreating(true);
    setFormError("");
    const body = {
      storeid: scope.storeid,
      role,
      maxuses: Number(maxUses),
    };
    if (note.trim()) body.note = note.trim();
    try {
      const res = await fleetApi.createCode(body);
      const { code, expiresat, maxuses } = res.data.data;
      setCreated({
        code,
        expiresat,
        maxuses: maxuses ?? body.maxuses,
        role: body.role,
      });
    } catch (err) {
      const text = fleetErrorText(err, "Could not create the code");
      setFormError(text);
      message.error(text);
    } finally {
      submitRef.current = false;
      setCreating(false);
    }
  };

  const done = () => {
    // The one-time code leaves React state now, not whenever the parent gets
    // around to closing the modal.
    setCreated(null);
    onCreated?.();
    onClose();
  };

  if (created) {
    const shown = formatCode(created.code);
    return (
      <TWModal
        open={open}
        onClose={() => {}}
        dismissible={false}
        size="md"
        title="Copy your code now"
        footer={
          <div className="flex flex-col-reverse gap-2 sm:flex-row sm:justify-end">
            <TWButton variant="primary" onClick={done}>
              Done
            </TWButton>
          </div>
        }
      >
        {/* gl-block: Gleap session replay masks this block (the code). */}
        <div data-testid="code-block" className="space-y-4 gl-block">
          <div
            role="alert"
            className="rounded-md bg-rose-50 px-3 py-2 text-sm text-rose-800 dark:bg-rose-500/10 dark:text-rose-300"
          >
            This code is shown once. It works {timesText(created.maxuses)} and
            expires {dayjs(created.expiresat).format(DATE_FORMAT)}.
          </div>
          <div>
            <div className="flex flex-wrap items-end gap-2">
              <TWInput
                label="Code"
                value={shown}
                readOnly
                size="lg"
                className="h-16 text-center font-mono text-2xl font-semibold tracking-widest sm:text-3xl"
                wrapperClassName="min-w-[12rem] flex-1"
                onFocus={(e) => e.target.select()}
              />
              <CopyButton text={shown} />
            </div>
            <p className="mt-1 text-xs text-slate-500 dark:text-slate-400">
              Type it on the tablet. Dashes and lower case are fine.
            </p>
          </div>
          <AgentApkRow url={apkUrl} apiOrigin={apiOrigin} />
          <SetupQr
            code={created.code}
            validUntil={dayjs(created.expiresat).format(DATE_FORMAT)}
            storeName={storeName}
            role={created.role}
            agentRelease={agentRelease}
            apiOrigin={apiOrigin}
          />
        </div>
      </TWModal>
    );
  }

  return (
    <TWModal
      open={open}
      onClose={creating ? () => {} : onClose}
      size="md"
      title="New enrollment code"
      description="Works up to the number of uses you set and expires in 24 hours."
      footer={
        <div className="flex flex-col-reverse gap-2 sm:flex-row sm:justify-end">
          <TWButton variant="ghost" onClick={onClose} disabled={creating}>
            Cancel
          </TWButton>
          <TWButton
            variant="primary"
            onClick={create}
            loading={creating}
            disabled={creating || !canCreate}
          >
            Create code
          </TWButton>
        </div>
      }
    >
      <div className="space-y-4">
        <div>
          <CompanyStoreSelect
            companyId={scope.companyid}
            storeId={scope.storeid}
            onChange={setScope}
            onStoreName={setStoreName}
            storeMode="single"
            placeholders={{ company: "Choose a company", store: "Choose a store" }}
          />
          <p className="mt-1 text-xs text-slate-500 dark:text-slate-400">
            Pick the store this tablet belongs to
          </p>
        </div>
        <TWSelect
          label="Role"
          options={ROLE_OPTIONS}
          value={role || undefined}
          placeholder="Choose a role"
          allowClear={false}
          onChange={(value) => setRole(value || "")}
        />
        <TWInput
          label="Max uses"
          type="number"
          min={1}
          max={50}
          step={1}
          value={maxUses}
          onChange={(e) => setMaxUses(e.target.value)}
          hint="1 to 50"
          error={maxUsesOk ? undefined : MAX_USES_ERROR}
        />
        <TWInput
          label="Note"
          value={note}
          onChange={(e) => setNote(e.target.value)}
          placeholder="e.g. Elk Grove kiosk 2"
          maxLength={200}
        />
        <AgentApkRow url={apkUrl} apiOrigin={apiOrigin} />
        {formError ? (
          <div
            role="alert"
            className="rounded-md bg-rose-50 px-3 py-2 text-sm text-rose-800 dark:bg-rose-500/10 dark:text-rose-300"
          >
            {formError}
          </div>
        ) : null}
      </div>
    </TWModal>
  );
};

export default EnrollmentCodeModal;
